import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Badge
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ProjectsList = () => {
    // State
    const [projects, setProjects] = useState([]);
    const [clients, setClients] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [projectLoading, setProjectLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentProject, setCurrentProject] = useState(null);

    const toast = useRef(null);


    // Form State
    const [formData, setFormData] = useState({
        project_name: "",
        client_id: "",
        start_date: "",
        deadline: "",
        price: "",
        labels: "",
        description: "",
        assigned_team_id: "",
        status: "open",
        priority: "medium",
        budget: "",
        estimated_hours: "",
        tags: ""
    });

    const breadcrumbItems = [
        { title: "Dashboards", link: "/dashboard" },
        { title: "Projects", link: "#!" },
    ];

    // Options
    const statusOptions = [
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'on_hold', label: 'On Hold' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
    ];

    const showToast = (severity, summary, detail) => {
        toast.current.show({ severity, summary, detail, life: 3000 });
    };

    // Fetch Data
    const fetchProjects = useCallback(async () => {
        setProjectLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/project/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.status === true) {
                setProjects(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            showToast("error", "Error", "Failed to fetch projects");
        } finally {
            setProjectLoading(false);
        }
    }, []);

    const fetchClients = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/client/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.status === true) {
                setClients(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    }, []);

    const fetchTeams = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/department/list`, { // Assuming teams are departments or similar
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.status === true) {
                setTeams(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching teams:", error);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
        fetchClients();
        fetchTeams();
    }, [fetchProjects, fetchClients, fetchTeams]);

    // Handlers
    const toggleModal = () => {
        setModal(!modal);
        if (!modal) resetForm();
    };

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const resetForm = () => {
        setFormData({
            project_name: "",
            client_id: "",
            start_date: "",
            deadline: "",
            price: "",
            labels: "",
            description: "",
            assigned_team_id: "",
            status: "open",
            priority: "medium",
            budget: "",
            estimated_hours: "",
            tags: ""
        });
        setIsEditing(false);
        setCurrentProject(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (value) => {
        setFormData(prev => ({ ...prev, description: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const url = isEditing
                ? `${API_BASE_URL}/project/update/${currentProject.id}`
                : `${API_BASE_URL}/project/add`;

            const response = await axios.post(url, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.status === true) {
                showToast("success", "Success", isEditing ? "Project updated" : "Project created");
                fetchProjects();
                toggleModal();
            } else {
                showToast("error", "Error", response.data.message || "Operation failed");
            }
        } catch (error) {
            console.error("Error saving project:", error);
            showToast("error", "Error", error.response?.data?.message || "Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const editProject = (project) => {
        setIsEditing(true);
        setCurrentProject(project);

        // Find client option to pre-select


        setFormData({
            project_name: project.project_name,
            client_id: project.client_id,
            start_date: project.start_date,
            deadline: project.deadline,
            price: project.price,
            labels: project.labels,
            description: project.description,
            assigned_team_id: project.assigned_team_id,
            status: project.status,
            priority: project.priority,
            budget: project.budget,
            estimated_hours: project.estimated_hours,
            tags: project.tags
        });

        // Handle Team pre-selection if needed


        toggleModal();
    };



    const confirmDelete = (project) => {
        setCurrentProject(project);
        toggleDeleteModal();
    };

    const handleDelete = async () => {
        if (!currentProject) return;
        setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await axios.delete(`${API_BASE_URL}/project/delete/${currentProject.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.status === true) {
                showToast("success", "Success", "Project deleted successfully");
                fetchProjects();
                toggleDeleteModal();
            } else {
                showToast("error", "Error", response.data.message || "Delete failed");
            }
        } catch (error) {
            console.error("Error deleting project:", error);
            showToast("error", "Error", "Delete failed");
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'info';
            case 'on_hold': return 'warning';
            case 'not_started': return 'secondary';
            case 'cancelled': return 'danger';
            case 'open': return 'primary';
            default: return 'light';
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Projects" breadcrumbItems={breadcrumbItems} />

                    <Row className="mb-3">
                        <Col sm={12}>
                            <div className="d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Projects List</h4>
                                <div className="page-title-right">
                                    <Button color="primary" onClick={toggleModal} className="btn btn-sm waves-effect waves-light">
                                        <i className="mdi mdi-plus me-1"></i> Add Project
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {projectLoading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <p className="mt-2">Loading projects...</p>
                        </div>
                    ) : (
                        <Row>
                            {projects.map((project) => (
                                <Col xl={4} sm={6} key={project.id}>
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex">
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <h5 className="text-truncate font-size-15">
                                                        <Link to={`/admin/projects/view/${project.id}`} className="text-dark">
                                                            {project.project_name}
                                                        </Link>
                                                    </h5>
                                                    <p className="text-muted mb-4">{project.client_name}</p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <Badge color={getStatusColor(project.status)}>
                                                        {project.status}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h5 className="font-size-14 mb-0">
                                                        Deadline: <span className="text-muted fw-normal">{project.deadline}</span>
                                                    </h5>
                                                </div>
                                                <div className="avatar-group">
                                                    {/* Placeholder for team members */}
                                                    <div className="avatar-group-item">
                                                        <div className="avatar-xs">
                                                            <span className="avatar-title rounded-circle bg-primary text-white font-size-16">
                                                                P
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-3 pt-3 border-top d-flex justify-content-between">
                                                <div>
                                                    <Badge color="light" className="text-body">
                                                        {project.priority && project.priority.toUpperCase()}
                                                    </Badge>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button color="light" size="sm" onClick={() => editProject(project)}>
                                                        <i className="mdi mdi-pencil" />
                                                    </Button>
                                                    <Button color="danger" size="sm" onClick={() => confirmDelete(project)}>
                                                        <i className="mdi mdi-trash-can" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Container>
            </div>

            {/* Add/Edit Modal */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>{isEditing ? "Edit Project" : "Add Project"}</ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Project Name</Label>
                                    <Input
                                        type="text"
                                        name="project_name"
                                        value={formData.project_name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Client</Label>
                                    <Input
                                        type="select"
                                        name="client_id"
                                        value={formData.client_id}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>{client.company}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Deadline</Label>
                                    <Input
                                        type="date"
                                        name="deadline"
                                        value={formData.deadline}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Price</Label>
                                    <Input
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Budget</Label>
                                    <Input
                                        type="number"
                                        name="budget"
                                        value={formData.budget}
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Estimated Hours</Label>
                                    <Input
                                        type="number"
                                        name="estimated_hours"
                                        value={formData.estimated_hours}
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Status</Label>
                                    <Input
                                        type="select"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        {statusOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Priority</Label>
                                    <Input
                                        type="select"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                    >
                                        {priorityOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Assigned Team (Opt)</Label>
                                    <Input
                                        type="select"
                                        name="assigned_team_id"
                                        value={formData.assigned_team_id}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Team</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>{team.name}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Tags (Comma separated)</Label>
                                    <Input
                                        type="text"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label>Description</Label>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={handleDescriptionChange}
                                style={{ height: "150px", marginBottom: "50px" }}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>
                            {isEditing ? "Update" : "Create"}
                            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this project? This action cannot be undone.
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={loading}>
                        Delete
                        {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default ProjectsList;